import {ViewController} from "data/types/structure";
import {ConnextraType, ModalType} from "data/enums";
import {inject, injectable} from "inversify";
import {action, makeAutoObservable} from "mobx";
import {Bindings} from "data/constants/bindings";
import type {IModalsStore} from "data/stores/modals/modals.store";
import React from "react";
import type {IUserStore, IUser} from "data/stores/user/user.store";
import {AxiosError} from "axios";
import type {IAxiosApiError} from "data/types/api";
import type {IPredictionsStore} from "data/stores/predictions/predictions.store";
import type {IProfile} from "data/providers/api/auth.api.provider";
import type {ILeaguesStore} from "data/stores/leagues/leagues.store";
import {createConnextraScriptTag} from "data/utils";

interface IModalTermsForm extends HTMLFormElement {
	terms: HTMLInputElement;
	marketingEmails?: HTMLInputElement;
	marketingOptInPickem: HTMLInputElement;
}

export interface IModalController extends ViewController {
	close: () => void;

	isModalOpen: (modal: ModalType) => boolean;
	handleTermsForm: (event: React.SyntheticEvent<IModalTermsForm>) => void;
	handleRemoveUserLeagueModal: () => void;
	handleLeaveLeagueModal: () => void;
	showPredictionShareModal: () => void;
	handleRemoveLeagueModal: () => void;

	get modalData(): unknown;
	get userName(): string | undefined;
	get user(): IUser | undefined;
	get profile(): IProfile | undefined;
}

@injectable()
export class ModalController implements IModalController {
	constructor(
		@inject(Bindings.ModalsStore) private _modalsStore: IModalsStore,
		@inject(Bindings.UserStore) private _userStore: IUserStore,
		@inject(Bindings.PredictionsStore) private _predictionsStore: IPredictionsStore,
		@inject(Bindings.LeaguesStore) private _leaguesStore: ILeaguesStore
	) {
		makeAutoObservable(this);
	}

	get modalData(): unknown {
		return this._modalsStore.modalContent;
	}

	get userName(): string | undefined {
		return this._userStore.user?.username;
	}

	get user(): IUser | undefined {
		return this._userStore.user;
	}
	//
	get profile() {
		return this._userStore.profile;
	}

	public close = (): void => {
		this._modalsStore.hideModal();
	};

	public isModalOpen = (modal: ModalType): boolean => {
		return this._modalsStore.modal === modal;
	};

	public showPredictionShareModal = (): void => {
		this._modalsStore.showModal(ModalType.PREDICTIONSSHARE);
	};

	@action handleTermsForm = (event: React.SyntheticEvent<IModalTermsForm>) => {
		event.preventDefault();

		const {terms, marketingOptInPickem, marketingEmails} = event.currentTarget;
		const date: IProfile = {
			terms: terms.checked,
			marketingOptInPickem: marketingOptInPickem.checked,
		};

		if (marketingEmails) {
			date.marketingEmails = marketingEmails.checked;

			if (marketingEmails.checked) {
				createConnextraScriptTag(ConnextraType.EMAIL_OPTIN);
			}
		}

		this._userStore
			.update(date)
			.then(() => {
				this.close();
			})
			.catch((e: unknown) => {
				const error = e as AxiosError<IAxiosApiError, unknown>;
				this._modalsStore.showAxiosError(error);
			})
			.finally(() => {
				window.location.reload();
			});
	};

	@action handleRemoveUserLeagueModal = () => {
		void this._leaguesStore.removeUserFromLeague().finally(() => this.close());
	};

	@action handleLeaveLeagueModal = () => {
		if (this._leaguesStore.selectedLeague?.id) {
			void this._leaguesStore
				.leaveLeagueById(this._leaguesStore.selectedLeague.id)
				.finally(() => {
					this.close();
					window.location.href = `${process.env.PUBLIC_URL || ""}/leagues`;
				});
		}
	};

	@action handleRemoveLeagueModal = () => {
		void this._leaguesStore.removeSelectedLeague().finally(() => {
			this.close();
			window.location.href = `${process.env.PUBLIC_URL || ""}/leagues`;
		});
	};

	dispose(): void {
		return;
	}

	init(param: void): void {
		return;
	}
}
